<template>
  <div class="ln-page">
    <div class="ln-wrap">
      <header class="ln-head" data-reveal>
        <div class="ln-head__text">
          <p class="ln-eyebrow">Dashboard</p>
          <h1 class="ln-display">Deep linking</h1>
          <p class="ln-lede">
            Connect an iOS or Android app so a short link can open it directly
            to a specific screen, falling back to the store when the app isn't
            installed.
          </p>
        </div>

        <div class="ln-head__actions">
          <button
            v-if="canWrite"
            type="button"
            class="ln-btn ln-btn--solid"
            @click="openDialog"
          >
            <v-icon size="small" class="ln-btn__icon">mdi-plus</v-icon>
            Add configuration
          </button>
        </div>
      </header>

      <!-- Initial loading -->
      <div
        v-if="loading && configs.length === 0"
        class="ln-panel ln-loading"
        data-reveal
      >
        <v-progress-circular
          indeterminate
          size="26"
          width="2"
          class="ln-loading__spinner"
        />
        <p class="ln-loading__text">Loading configurations…</p>
      </div>

      <!-- Empty state -->
      <div v-else-if="configs.length === 0" class="ln-panel" data-reveal>
        <div class="ln-empty">
          <svg
            class="ln-empty__art"
            width="96"
            height="96"
            viewBox="0 0 96 96"
            aria-hidden="true"
            focusable="false"
          >
            <rect
              x="56"
              y="10"
              width="28"
              height="28"
              fill="var(--ln-yellow-bg)"
            />
            <g
              fill="none"
              stroke="var(--ln-ink)"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect x="32" y="22" width="28" height="50" rx="5" />
              <line x1="32" y1="61" x2="60" y2="61" />
              <path d="M22 47l-9 0m0 0l4.5-4.5m-4.5 4.5l4.5 4.5" />
            </g>
          </svg>
          <p class="ln-empty__title">No deep linking configurations yet</p>
          <p class="ln-empty__body">
            Add a configuration to let a short link open your app directly to a
            specific screen instead of just the store listing.
          </p>
          <button
            v-if="canWrite"
            type="button"
            class="ln-btn ln-btn--solid"
            @click="openDialog"
          >
            <v-icon size="small" class="ln-btn__icon">mdi-plus</v-icon>
            Add configuration
          </button>
        </div>
      </div>

      <!-- Config cards -->
      <section v-else class="ln-grid" data-reveal>
        <article
          v-for="(config, index) in configs"
          :key="index"
          class="ln-card"
        >
          <div class="ln-card__head">
            <div class="ln-card__app">
              <div class="ln-card__app-icon">
                <img
                  v-if="config.iconUrl"
                  :src="config.iconUrl"
                  :alt="config.appName + ' icon'"
                />
                <v-icon v-else size="small">mdi-apps</v-icon>
              </div>
              <div class="ln-card__app-info">
                <p class="ln-card__app-name" :title="config.appName">
                  {{ config.appName }}
                </p>
                <div class="ln-card__app-domain">
                  <span>{{ config.domain }}</span>
                  <button
                    type="button"
                    class="ln-iconbtn ln-iconbtn--xs"
                    aria-label="Copy domain"
                    @click="copyDomain(config.domain)"
                  >
                    <v-icon size="x-small">mdi-content-copy</v-icon>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="ln-card__body">
            <div class="ln-platform-row">
              <v-icon size="small" class="ln-platform-row__icon"
                >mdi-apple</v-icon
              >
              <div class="ln-platform-row__info">
                <p class="ln-platform-row__label">iOS bundle ID</p>
                <p class="ln-platform-row__value">
                  {{ config.iosConfigs[0].bundleId }}
                </p>
                <p
                  v-if="!config.iosConfigs.length"
                  class="ln-platform-row__empty"
                >
                  No iOS config
                </p>
              </div>
            </div>

            <div class="ln-platform-row">
              <v-icon
                size="small"
                class="ln-platform-row__icon ln-platform-row__icon--android"
                >mdi-android</v-icon
              >
              <div class="ln-platform-row__info">
                <p class="ln-platform-row__label">Android package name</p>
                <p class="ln-platform-row__value">
                  {{ config.androidConfigs[0].packageName }}
                </p>
                <p
                  v-if="!config.androidConfigs.length"
                  class="ln-platform-row__empty"
                >
                  No Android config
                </p>
              </div>
            </div>
          </div>

          <div class="ln-card__footer">
            <v-menu location="bottom">
              <template v-slot:activator="{ props }">
                <button
                  type="button"
                  class="ln-btn ln-btn--quiet ln-btn--sm"
                  v-bind="props"
                >
                  <v-icon size="x-small" class="ln-btn__icon"
                    >mdi-cog-outline</v-icon
                  >
                  Setup
                  <v-icon size="x-small" class="ln-btn__icon"
                    >mdi-menu-down</v-icon
                  >
                </button>
              </template>
              <div class="ln-menu">
                <button
                  type="button"
                  class="ln-menu__item"
                  @click="viewSetup('ios', config)"
                >
                  <v-icon size="small">mdi-apple</v-icon>
                  iOS setup
                </button>
                <button
                  type="button"
                  class="ln-menu__item"
                  @click="viewSetup('android', config)"
                >
                  <v-icon size="small">mdi-android</v-icon>
                  Android setup
                </button>
              </div>
            </v-menu>

            <div class="ln-actions">
              <v-tooltip v-if="canWrite" location="bottom">
                <template v-slot:activator="{ props }">
                  <button
                    type="button"
                    class="ln-iconbtn"
                    aria-label="Edit"
                    @click="editConfig(index)"
                    v-bind="props"
                  >
                    <v-icon size="small">mdi-pencil</v-icon>
                  </button>
                </template>
                <span>Edit</span>
              </v-tooltip>
              <v-tooltip v-if="canWrite" location="bottom">
                <template v-slot:activator="{ props }">
                  <button
                    type="button"
                    class="ln-iconbtn ln-iconbtn--danger"
                    aria-label="Delete"
                    @click="confirmDeleteDialog(config)"
                    v-bind="props"
                  >
                    <v-icon size="small">mdi-delete</v-icon>
                  </button>
                </template>
                <span>Delete</span>
              </v-tooltip>
            </div>
          </div>
        </article>
      </section>
    </div>

    <!-- Add/Edit config dialog -->
    <v-dialog v-model="dialog" max-width="700" persistent>
      <div class="ln-dialog unmask-form">
        <div class="ln-dialog__head">
          <h2 class="ln-dialog__title">
            <v-icon class="ln-dialog__title-icon">mdi-apps</v-icon>
            {{
              editingIndex === null
                ? "Add deep linking configuration"
                : "Edit deep linking configuration"
            }}
          </h2>
          <button
            type="button"
            class="ln-iconbtn"
            aria-label="Close"
            @click="closeDialog"
          >
            <v-icon size="small">mdi-close</v-icon>
          </button>
        </div>

        <div class="ln-dialog__body">
          <div class="ln-banner ln-banner--blue ln-dialog__banner">
            <v-icon size="small">mdi-information-outline</v-icon>
            <span>
              Your app must handle incoming deep linking paths that start with
              <strong>/d/</strong> (for example: <code>/d/product/123</code>).
              In the Links section you can then create a link and select the
              deep linking domain configured here. Both apps must already be
              published on the App Store and Google Play.
            </span>
          </div>

          <v-form
            id="deepLinkForm"
            ref="form"
            v-model="valid"
            @submit.prevent="saveConfig"
          >
            <p class="ln-field-label">Domain</p>
            <div v-if="!isManagedDomain" class="ln-domain-row">
              <v-text-field
                v-model="formData.subdomain"
                label="Subdomain"
                :rules="[
                  (v) => !!v || 'Subdomain is required',
                  (v) =>
                    /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(v) ||
                    'Invalid subdomain format',
                ]"
                :required="true"
                :disabled="!hasPaidPlan || editingIndex !== null"
                variant="outlined"
                density="compact"
                hide-details="auto"
                class="ln-dialog__field ln-dialog__field--inline ln-domain-row__subdomain"
              />
              <span class="ln-domain-row__dot">.</span>
              <v-select
                v-model="selectedDomain"
                :items="domainOptions"
                :disabled="editingIndex !== null"
                label="Domain"
                variant="outlined"
                density="compact"
                hide-details="auto"
                class="ln-dialog__field ln-dialog__field--inline ln-domain-row__select"
                @update:model-value="onDomainChange"
              />
              <button
                type="button"
                class="ln-iconbtn"
                aria-label="Copy to clipboard"
                @click="copyToClipboard(fullDomain)"
              >
                <v-icon size="x-small">mdi-content-copy</v-icon>
              </button>
            </div>

            <div v-else class="ln-domain-row">
              <v-select
                v-model="selectedDomain"
                :items="domainOptions"
                :disabled="editingIndex !== null"
                label="Domain"
                variant="outlined"
                density="compact"
                hide-details="auto"
                class="ln-dialog__field ln-dialog__field--inline ln-domain-row__select ln-domain-row__select--full"
                @update:model-value="onDomainChange"
              />
              <button
                type="button"
                class="ln-iconbtn"
                aria-label="Copy to clipboard"
                @click="copyToClipboard(selectedDomain)"
              >
                <v-icon size="x-small">mdi-content-copy</v-icon>
              </button>
            </div>

            <p
              v-if="!isManagedDomain && !hasPaidPlan && editingIndex === null"
              class="ln-dialog__note"
            >
              <v-icon size="x-small">mdi-lock-outline</v-icon>
              Upgrade to customize the subdomain.
            </p>

            <v-text-field
              v-model="formData.appName"
              label="App Name"
              :rules="[
                (v) => !!v || 'App name is required',
                (v) =>
                  (v && v.length >= 5) ||
                  'App name must be at least 5 characters',
              ]"
              required
              variant="outlined"
              density="compact"
              hint="This will be the display name for your deep link configuration, for internal use only."
              persistent-hint
              class="ln-dialog__field ln-dialog__field--spaced"
            />

            <div class="ln-config-divider"></div>
            <div class="ln-config-section-head">
              <v-icon size="small" class="ln-config-section-icon"
                >mdi-apple</v-icon
              >
              <h3 class="ln-config-section-title">iOS configuration</h3>
              <button
                type="button"
                class="ln-text-link"
                @click="openDrawer('ios')"
              >
                Setup guide
              </button>
            </div>
            <div v-if="formData.iosConfigs.length" class="ln-config-fields">
              <v-text-field
                v-model="formData.iosConfigs[0].teamId"
                label="Team ID"
                placeholder="AB12CD34EF"
                variant="outlined"
                density="compact"
                hide-details="auto"
                prepend-inner-icon="mdi-identifier"
                :rules="[
                  (v) => !!v || 'Team ID is required',
                  (v) =>
                    (v && v.length === 10) ||
                    'Team ID must be exactly 10 characters',
                ]"
                required
                class="ln-dialog__field"
              >
                <template v-slot:append-inner>
                  <v-menu location="bottom">
                    <template v-slot:activator="{ props }">
                      <button
                        type="button"
                        class="ln-help-trigger"
                        v-bind="props"
                        aria-label="How to find your Team ID"
                      >
                        <v-icon size="x-small">mdi-help-circle-outline</v-icon>
                      </button>
                    </template>
                    <div class="ln-help-popover">
                      <p class="ln-help-popover__title">Finding your Team ID</p>
                      <p class="ln-help-popover__body">
                        Log in to your
                        <a
                          href="https://developer.apple.com/account/#/membership"
                          target="_blank"
                          rel="noopener"
                          >Apple Developer Account</a
                        >
                        and go to Membership. Your Team ID is listed there.
                      </p>
                    </div>
                  </v-menu>
                </template>
              </v-text-field>

              <v-text-field
                v-model="formData.iosConfigs[0].bundleId"
                label="Bundle ID"
                placeholder="com.company.appname"
                variant="outlined"
                density="compact"
                hide-details="auto"
                prepend-inner-icon="mdi-apple-ios"
                :rules="[(v) => !!v || 'Bundle ID is required']"
                required
                class="ln-dialog__field"
              >
                <template v-slot:append-inner>
                  <v-menu location="bottom">
                    <template v-slot:activator="{ props }">
                      <button
                        type="button"
                        class="ln-help-trigger"
                        v-bind="props"
                        aria-label="How to find your iOS Bundle ID"
                      >
                        <v-icon size="x-small">mdi-help-circle-outline</v-icon>
                      </button>
                    </template>
                    <div class="ln-help-popover">
                      <p class="ln-help-popover__title">
                        Finding your iOS Bundle ID
                      </p>
                      <p class="ln-help-popover__body">
                        Log in to
                        <a
                          href="https://appstoreconnect.apple.com"
                          target="_blank"
                          rel="noopener"
                          >App Store Connect</a
                        >
                        and select your app. Under "App Information" you can
                        find the Bundle ID.
                      </p>
                    </div>
                  </v-menu>
                </template>
              </v-text-field>
            </div>

            <div class="ln-config-divider"></div>
            <div class="ln-config-section-head">
              <v-icon
                size="small"
                class="ln-config-section-icon ln-config-section-icon--android"
                >mdi-android</v-icon
              >
              <h3 class="ln-config-section-title">Android configuration</h3>
              <button
                type="button"
                class="ln-text-link"
                @click="openDrawer('android')"
              >
                Setup guide
              </button>
            </div>
            <div v-if="formData.androidConfigs.length" class="ln-config-fields">
              <v-text-field
                v-model="formData.androidConfigs[0].packageName"
                label="Package Name"
                placeholder="com.company.appname"
                variant="outlined"
                density="compact"
                hide-details="auto"
                prepend-inner-icon="mdi-android"
                :rules="[(v) => !!v || 'Package name is required']"
                required
                class="ln-dialog__field"
              >
                <template v-slot:append-inner>
                  <v-menu location="bottom">
                    <template v-slot:activator="{ props }">
                      <button
                        type="button"
                        class="ln-help-trigger"
                        v-bind="props"
                        aria-label="How to find your Android package name"
                      >
                        <v-icon size="x-small">mdi-help-circle-outline</v-icon>
                      </button>
                    </template>
                    <div class="ln-help-popover">
                      <p class="ln-help-popover__title">
                        Finding your package name
                      </p>
                      <p class="ln-help-popover__body">
                        The package name is defined in your
                        <code>AndroidManifest.xml</code> or in your
                        <code>build.gradle</code> file.
                      </p>
                    </div>
                  </v-menu>
                </template>
              </v-text-field>

              <v-textarea
                v-model="formData.androidConfigs[0].certFingerprintsText"
                label="Certificate Fingerprints"
                placeholder="SHA-256 fingerprint (one per line)"
                variant="outlined"
                density="compact"
                hide-details="auto"
                prepend-inner-icon="mdi-fingerprint"
                auto-grow
                :rules="[
                  (v) =>
                    (v && v.trim().length > 0) ||
                    'At least one fingerprint is required',
                ]"
                required
                class="ln-dialog__field"
                @update:model-value="
                  onCertFingerprintsInput(formData.androidConfigs[0])
                "
              >
                <template v-slot:append-inner>
                  <v-menu location="bottom" :close-on-content-click="false">
                    <template v-slot:activator="{ props }">
                      <button
                        type="button"
                        class="ln-help-trigger"
                        v-bind="props"
                        aria-label="How to get your SHA-256 fingerprint"
                      >
                        <v-icon size="x-small">mdi-help-circle-outline</v-icon>
                      </button>
                    </template>
                    <div class="ln-help-popover">
                      <p class="ln-help-popover__title">
                        Getting your SHA-256 fingerprint
                      </p>
                      <p class="ln-help-popover__body">
                        Apps installed from Google Play are signed by Google, so
                        use the key Google signs with: in
                        <a
                          href="https://play.google.com/console/"
                          target="_blank"
                          rel="noopener"
                          >Play Console</a
                        >, open your app, go to
                        <strong>Release → Setup → App signing</strong> and copy
                        the SHA-256 of the
                        <strong>App signing key certificate</strong>.
                      </p>
                      <p class="ln-help-popover__body">
                        To test builds you install yourself, add their
                        fingerprint on another line (run
                        <code>./gradlew signingReport</code> in your android
                        directory). Learn more
                        <a
                          href="https://developer.android.com/training/app-links/configure-assetlinks"
                          target="_blank"
                          rel="noopener"
                          >here</a
                        >.
                      </p>
                    </div>
                  </v-menu>
                </template>
              </v-textarea>
            </div>

            <div
              v-if="error"
              class="ln-banner ln-banner--red ln-dialog__banner ln-dialog__banner--spaced"
            >
              <v-icon size="small">mdi-alert-circle</v-icon>
              <span>{{ error }}</span>
            </div>
          </v-form>
        </div>

        <div class="ln-dialog__actions">
          <button
            type="button"
            class="ln-btn ln-btn--quiet"
            @click="closeDialog"
            :disabled="loading"
          >
            Cancel
          </button>
          <!-- Vuetify 2's `lazy-validation` kept `valid` true until the form
               was validated; Vuetify 4's v-form model starts as null (nothing
               validated yet) and `!valid` disabled the button the moment the
               dialog opened. Disable only on a real failure - saveConfig()
               validates before it submits either way. -->
          <button
            type="submit"
            form="deepLinkForm"
            class="ln-btn ln-btn--solid"
            :disabled="valid === false || loading"
          >
            <v-icon
              size="small"
              class="ln-btn__icon"
              :class="{ 'ln-spin': loading }"
              >{{
                loading
                  ? "mdi-loading"
                  : editingIndex === null
                    ? "mdi-plus"
                    : "mdi-content-save"
              }}</v-icon
            >
            {{ editingIndex === null ? "Create" : "Save" }}
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Delete config dialog -->
    <v-dialog v-model="deleteDialog" max-width="480" persistent>
      <div class="ln-dialog">
        <h2 class="ln-dialog__title">Delete configuration</h2>
        <div class="ln-dialog__body">
          <p>
            This will permanently delete this configuration and all the
            <strong>links</strong> created with it. This cannot be undone, and
            the domain name cannot be reused afterwards.
          </p>
          <p class="ln-dialog__note">
            Clicks already recorded by links created with this configuration
            will be retained.
          </p>
          <p class="ln-dialog__label">App</p>
          <p class="ln-dialog__target">
            {{ configToDelete && configToDelete.appName }}
          </p>
          <p class="ln-dialog__label">Domain</p>
          <p class="ln-dialog__target">
            {{ configToDelete && configToDelete.domain }}
          </p>
        </div>
        <div class="ln-dialog__actions">
          <button
            type="button"
            class="ln-btn ln-btn--quiet"
            @click="deleteDialog = false"
            :disabled="loading"
          >
            Cancel
          </button>
          <button
            type="button"
            class="ln-btn ln-btn--danger"
            :disabled="loading"
            @click="confirmDelete"
          >
            <v-icon
              size="small"
              class="ln-btn__icon"
              :class="{ 'ln-spin': loading }"
              >{{ loading ? "mdi-loading" : "mdi-delete" }}</v-icon
            >
            Yes, delete configuration
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Setup instructions drawer -->
    <v-navigation-drawer
      v-model="drawer"
      location="right"
      temporary
      width="640"
      class="ln-setup-drawer"
    >
      <div class="ln-drawer__head">
        <p class="ln-drawer__title">
          <span v-if="drawerPlatform === 'ios'">iOS deep linking setup</span>
          <span v-else-if="drawerPlatform === 'android'"
            >Android deep linking setup</span
          >
        </p>
        <button
          type="button"
          class="ln-iconbtn"
          aria-label="Close"
          @click="drawer = false"
        >
          <v-icon size="small">mdi-close</v-icon>
        </button>
      </div>

      <div class="ln-drawer__body">
        <div v-if="drawerPlatform === 'ios'">
          <h3>1) Authorize your domain (Universal Links)</h3>
          <p>
            Authorize <strong>{{ guideDomain }}</strong> with your iOS app using
            Associated Domains. Our platform hosts the required well-known file
            (<code>apple-app-site-association</code>), so you do
            <strong>not</strong> need to host it.
          </p>

          <p><strong>Native</strong></p>
          <ol>
            <li>
              Open your Xcode project →
              <strong>Signing &amp; Capabilities → Associated Domains</strong>.
            </li>
            <li>
              Add the entry:
              <div>
                <code>applinks:{{ guideDomain }}</code>
              </div>
            </li>
            <li>Build and install the app on a device or TestFlight build.</li>
          </ol>
          <p>
            Apple caches this file, so a change to this configuration can take a
            while to reach devices: an installed app picks it up when it is
            reinstalled or updated.
          </p>

          <p>
            <strong>Entitlements file</strong>
            (<code>YourApp.entitlements</code>)
          </p>
          <pre class="ln-code">
...
&lt;plist version="1.0"&gt;
  &lt;dict&gt;
    &lt;key&gt;com.apple.developer.associated-domains&lt;/key&gt;
    &lt;array&gt;
      &lt;string&gt;applinks:{{ guideDomain }}&lt;/string&gt;
    &lt;/array&gt;
  &lt;/dict&gt;
&lt;/plist&gt;
...
          </pre>
          <p><strong>Expo</strong> (app.json/app.config.js)</p>
          <pre class="ln-code">
{
  "expo": {
    "ios": {
      "associatedDomains": ["applinks:{{ guideDomain }}"]
    }
  }
}
          </pre>

          <h3>2) Handle deep links in your app</h3>
          <div class="ln-banner ln-banner--blue">
            <v-icon size="x-small">mdi-information-outline</v-icon>
            <span
              >Only paths under <strong>/d/</strong> are routed to your app.
              Example: <code>https://{{ guideDomain }}/d/product/123</code>.
              Links arrive with extra query parameters (<code>from_1link</code>,
              and any <code>utm_*</code> the short link was opened with), so
              make sure your routing accepts them.</span
            >
          </div>
          <p>SwiftUI:</p>
          <pre class="ln-code">
WindowGroup {
  ContentView()
    .onOpenURL { url in
      handleDeepLink(url: url) // parse /d/... and route
    }
}
          </pre>
          <p>UIKit with scenes (SceneDelegate):</p>
          <pre class="ln-code">
// App launched by the link
func scene(_ scene: UIScene, willConnectTo session: UISceneSession,
           options connectionOptions: UIScene.ConnectionOptions) {
  if let userActivity = connectionOptions.userActivities.first(where: {
       $0.activityType == NSUserActivityTypeBrowsingWeb }),
     let incomingURL = userActivity.webpageURL {
    handleDeepLink(url: incomingURL)
  }
}

// App already running
func scene(_ scene: UIScene, continue userActivity: NSUserActivity) {
  if userActivity.activityType == NSUserActivityTypeBrowsingWeb,
     let incomingURL = userActivity.webpageURL {
    handleDeepLink(url: incomingURL)
  }
}
          </pre>
          <p>UIKit without scenes (AppDelegate):</p>
          <pre class="ln-code">
func application(_ application: UIApplication,
                 continue userActivity: NSUserActivity,
                 restorationHandler: @escaping ([UIUserActivityRestoring]?) -> Void) -> Bool {
  if userActivity.activityType == NSUserActivityTypeBrowsingWeb,
     let incomingURL = userActivity.webpageURL {
    handleDeepLink(url: incomingURL)
    return true
  }
  return false
}
          </pre>

          <p><strong>Popular frameworks</strong></p>
          <p><strong>React Native</strong> (React Navigation)</p>
          <pre class="ln-code">
const linking = {
  prefixes: ['https://{{ guideDomain }}'],
  config: {
    screens: {
      // https://{{ guideDomain }}/d/product/123
      Product: 'd/product/:id',
    },
  },
};

&lt;NavigationContainer linking={linking}&gt;{/* ... */}&lt;/NavigationContainer&gt;
          </pre>
          <p>
            With Expo Router, place the screens under <code>app/d/</code> (for
            example <code>app/d/product/[id].tsx</code>).
          </p>
          <p>
            Learn more:
            <a
              href="https://reactnavigation.org/docs/deep-linking/"
              target="_blank"
              rel="noopener"
              >React Navigation Deep Linking</a
            >
            |
            <a
              href="https://reactnavigation.org/docs/configuring-links/"
              target="_blank"
              rel="noopener"
              >Handle deep linking</a
            >
          </p>
          <p><strong>Flutter</strong> (app_links 6 or later)</p>
          <pre class="ln-code">
import 'package:app_links/app_links.dart';

void initDeepLinks() {
  // receives the link that launched the app, then every later one
  AppLinks().uriLinkStream.listen((Uri uri) {
    if (uri.host == '{{ guideDomain }}' &amp;&amp; uri.path.startsWith('/d/')) {
      handleDeepLink(uri);
    }
  });
}
          </pre>
          <p><strong>Capacitor</strong> (@capacitor/app)</p>
          <pre class="ln-code">
import { App } from '@capacitor/app';

App.addListener('appUrlOpen', (event) => {
  const url = new URL(event.url);
  if (url.pathname.startsWith('/d/')) {
    handleDeepLink(url);
  }
});
          </pre>

          <p>
            See Apple's guide:
            <a
              href="https://developer.apple.com/documentation/xcode/supporting-universal-links-in-your-app"
              target="_blank"
              rel="noopener"
              >Supporting Universal Links</a
            >.
          </p>
        </div>
        <div v-else-if="drawerPlatform === 'android'">
          <h3>1) Authorize your domain (App Links)</h3>
          <p>
            Authorize <strong>{{ guideDomain }}</strong> with your Android app
            using App Links. Our platform hosts the required well-known file
            (<code>assetlinks.json</code>), so you do <strong>not</strong> need
            to host it.
          </p>
          <ol>
            <li>
              Add an intent filter to your <code>AndroidManifest.xml</code> on
              your main activity.
            </li>
            <li>
              Include the <strong>/d/</strong> path prefix so only deep-linking
              paths route to your app.
            </li>
          </ol>
          <p><strong>Manifest example</strong></p>
          <pre class="ln-code">
&lt;intent-filter android:autoVerify="true"&gt;
  &lt;action android:name="android.intent.action.VIEW" /&gt;
  &lt;category android:name="android.intent.category.DEFAULT" /&gt;
  &lt;category android:name="android.intent.category.BROWSABLE" /&gt;
  &lt;data android:scheme="https"
        android:host="{{ guideDomain }}"
        android:pathPrefix="/d/" /&gt;
&lt;/intent-filter&gt;
          </pre>

          <p><strong>Expo</strong> (app.json/app.config.js)</p>
          <pre class="ln-code">
{
  "expo": {
    "android": {
      "intentFilters": [
        {
          "action": "VIEW",
          "autoVerify": true,
          "data": [
            {
              "scheme": "https",
              "host": "{{ guideDomain }}",
              "pathPrefix": "/d/"
            }
          ],
          "category": ["BROWSABLE", "DEFAULT"]
        }
      ]
    }
  }
}
          </pre>

          <h3>2) Handle deep links in your app</h3>
          <div class="ln-banner ln-banner--blue">
            <v-icon size="x-small">mdi-information-outline</v-icon>
            <span
              >Only paths under <strong>/d/</strong> are routed to your app.
              Example: <code>https://{{ guideDomain }}/d/product/123</code>.
              Links arrive with extra query parameters (<code>from_1link</code>,
              and any <code>utm_*</code> the short link was opened with), so
              make sure your routing accepts them.</span
            >
          </div>
          <p>Native example (Kotlin):</p>
          <pre class="ln-code">
// App launched by the link
override fun onCreate(savedInstanceState: Bundle?) {
  super.onCreate(savedInstanceState)
  handleIntent(intent)
}

// App already running (launchMode singleTop or singleTask)
override fun onNewIntent(intent: Intent) {
  super.onNewIntent(intent)
  handleIntent(intent)
}

private fun handleIntent(intent: Intent) {
  val uri = intent.data ?: return
  if (uri.path?.startsWith("/d/") == true) {
    handleDeepLink(uri)
  }
}
          </pre>

          <p><strong>Popular frameworks</strong></p>
          <p><strong>React Native</strong> (React Navigation)</p>
          <pre class="ln-code">
const linking = {
  prefixes: ['https://{{ guideDomain }}'],
  config: {
    screens: {
      // https://{{ guideDomain }}/d/product/123
      Product: 'd/product/:id',
    },
  },
};

&lt;NavigationContainer linking={linking}&gt;{/* ... */}&lt;/NavigationContainer&gt;
          </pre>
          <p>
            With Expo Router, place the screens under <code>app/d/</code> (for
            example <code>app/d/product/[id].tsx</code>).
          </p>
          <p>
            Learn more:
            <a
              href="https://reactnavigation.org/docs/deep-linking/"
              target="_blank"
              rel="noopener"
              >React Navigation Deep Linking</a
            >
            |
            <a
              href="https://reactnavigation.org/docs/configuring-links/"
              target="_blank"
              rel="noopener"
              >Handle deep linking</a
            >
          </p>

          <p><strong>Flutter</strong> (app_links 6 or later)</p>
          <pre class="ln-code">
import 'package:app_links/app_links.dart';

void initDeepLinks() {
  // receives the link that launched the app, then every later one
  AppLinks().uriLinkStream.listen((Uri uri) {
    if (uri.host == '{{ guideDomain }}' &amp;&amp; uri.path.startsWith('/d/')) {
      handleDeepLink(uri);
    }
  });
}
          </pre>

          <p><strong>Capacitor</strong> (@capacitor/app)</p>
          <pre class="ln-code">
import { App } from '@capacitor/app';

App.addListener('appUrlOpen', (event) =&gt; {
  const url = new URL(event.url);
  if (url.pathname.startsWith('/d/')) {
    handleDeepLink(url);
  }
});
          </pre>

          <p>
            See Android's guide:
            <a
              href="https://developer.android.com/training/app-links#web-links"
              target="_blank"
              rel="noopener"
              >Android App Links</a
            >.
          </p>
        </div>
      </div>
    </v-navigation-drawer>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import {
  getDeepLinkingDomains,
  createDeepLinkingConfig,
  updateDeepLinkingConfig,
  deleteDeepLinkingConfig,
} from "@/api";

export default {
  name: "DeepLinking",
  data() {
    return {
      loading: false,
      error: "",

      dialog: false,
      deleteDialog: false,
      valid: true,
      editingIndex: null,
      configToDelete: null,
      configs: [],
      selectedDomain: "",
      formData: {
        subdomain: "",
        appName: "",
        iosConfigs: [
          {
            teamId: "",
            bundleId: "",
          },
        ],
        androidConfigs: [
          {
            packageName: "",
            certFingerprints: [],
            certFingerprintsText: "",
          },
        ],
      },
      defaultFormData: {
        subdomain: "",
        appName: "",
        iosConfigs: [
          {
            teamId: "",
            bundleId: "",
          },
        ],
        androidConfigs: [
          {
            packageName: "",
            certFingerprints: [],
            certFingerprintsText: "",
          },
        ],
      },
      drawer: false,
      drawerPlatform: null, // 'ios' or 'android'
    };
  },
  computed: {
    ...mapState([
      "userPlan",
      "availableDomains",
      "availableDomainsStrippedDown",
    ]),
    ...mapGetters(["canWrite"]),
    domainOptions() {
      // Use strings array if available; fallback to mapping objects
      if (
        Array.isArray(this.availableDomainsStrippedDown) &&
        this.availableDomainsStrippedDown.length
      ) {
        return this.availableDomainsStrippedDown.filter(
          (d) => !d.endsWith(".link-to.app"),
        );
      }
      return (this.availableDomains || [])
        .map((d) => d.domain)
        .filter((d) => !d.endsWith(".link-to.app"));
    },
    isManagedDomain() {
      return (
        typeof this.selectedDomain === "string" &&
        !this.selectedDomain.endsWith("link-to.app")
      );
    },
    fullDomain() {
      // What to show in UI copy button when on the main form row
      return this.isManagedDomain
        ? this.selectedDomain || ""
        : `${this.formData.subdomain}.${this.selectedDomain}`;
    },
    guideDomain() {
      // the setup guide can be opened before a domain (or subdomain) is set
      const domain = this.fullDomain;
      if (!this.selectedDomain || !domain || domain.startsWith(".")) {
        return "YOUR_DOMAIN";
      }
      return domain;
    },
    hasPaidPlan() {
      const planName =
        this.userPlan && this.userPlan.plan && this.userPlan.plan.name;
      return typeof planName === "string" && planName.toLowerCase() !== "free";
    },
  },
  methods: {
    showSuccess(message) {
      this.$store.commit("showSnackbarMessage", {
        content: message,
        color: "success",
      });
    },
    showError(message) {
      this.$store.commit("showSnackbarMessage", {
        content: message,
        color: "error",
      });
    },
    setupReveal() {
      // called again on every fetchConfigs() (add/edit/delete), not just once
      // on mount like the other ln- pages: drop the previous observer first
      // or each refetch would pile up another one watching stale elements
      if (this._revealObserver) {
        this._revealObserver.disconnect();
        this._revealObserver = null;
      }

      const elements = this.$el.querySelectorAll("[data-reveal]");
      const prefersReducedMotion =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      // no observer (or the user asked for no motion): show everything at once
      // rather than leaving the page permanently at opacity 0
      if (!("IntersectionObserver" in window) || prefersReducedMotion) {
        elements.forEach((el) => el.classList.add("is-revealed"));
        return;
      }

      this._revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-revealed");
            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.05 },
      );
      elements.forEach((el) => this._revealObserver.observe(el));
    },
    openDrawer(platform) {
      this.drawerPlatform = platform;
      this.drawer = true;
    },
    extractSubdomainFromDomain(domain) {
      if (!domain || typeof domain !== "string") return "";
      return domain.endsWith(".link-to.app")
        ? domain.replace(".link-to.app", "")
        : domain;
    },
    viewSetup(platform, config) {
      // Ensure drawer shows instructions for the selected config's domain
      const sub = this.extractSubdomainFromDomain(config && config.domain);
      if (sub) {
        this.formData.subdomain = sub;
      }
      // Also set selectedDomain for correct rendering in helpers
      if (config && config.domain) {
        if (config.domain.endsWith(".link-to.app")) {
          this.selectedDomain = "link-to.app";
        } else {
          this.selectedDomain = config.domain;
        }
      }
      this.drawerPlatform = platform;
      this.drawer = true;
    },
    onCertFingerprintsInput(androidConfig) {
      if (typeof androidConfig.certFingerprintsText === "string") {
        const all = androidConfig.certFingerprintsText
          .split("\n")
          .map((f) => f.trim())
          .filter((f) => f.length > 0);
        androidConfig.certFingerprints = all.filter(
          (f) => f.length >= 64 && f.length <= 95,
        );
        if (all.length !== androidConfig.certFingerprints.length) {
          this.error = "Each fingerprint must be between 64 and 95 characters.";
        } else {
          this.error = "";
        }
      }
    },
    generateRandomSubdomain() {
      const randomString = Math.random().toString(36).substring(2, 8);
      const newSubdomain = `${randomString}1l`;
      this.formData.subdomain = newSubdomain;
      // Save to local storage when generating a new subdomain
      localStorage.setItem("pendingSubdomain", newSubdomain);
      return newSubdomain; // Return the generated subdomain for other methods to use
    },
    // getFullDomain(subdomain) {
    //   if (this.isManagedDomain) {
    //     return subdomain ? `${subdomain}.${this.selectedDomain}` : "";
    //   }
    //   return this.selectedDomain || "";
    // },

    async fetchConfigs() {
      this.loading = true;
      this.error = "";
      try {
        const configs = await getDeepLinkingDomains();
        // Ensure each config has the required arrays with defaults
        this.configs = (configs.result || []).map((config) => ({
          ...config,
          iosConfigs: config.iosConfigs || [],
          androidConfigs: config.androidConfigs || [],
        }));
      } catch (error) {
        this.error = "Failed to load deep linking configurations";
        console.error("Error fetching configs:", error);
      } finally {
        this.loading = false;
        this.$nextTick(() => this.setupReveal());
      }
    },
    openDialog() {
      this.editingIndex = null;
      this.formData = JSON.parse(JSON.stringify(this.defaultFormData));
      this.selectedDomain = "";
      this.dialog = true;
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }
      });
    },
    editConfig(index) {
      this.editingIndex = index;
      const config = JSON.parse(JSON.stringify(this.configs[index]));

      // Extract subdomain from domain
      if (config.domain) {
        // Remove .link-to.app suffix if present
        if (config.domain.endsWith(".link-to.app")) {
          config.subdomain = config.domain.replace(".link-to.app", "");
          this.selectedDomain = "link-to.app";
        } else {
          // custom domain
          config.subdomain = "";
          this.selectedDomain = config.domain;
        }
      }

      // Android certificate fingerprints text
      if (config.androidConfigs && config.androidConfigs.length > 0) {
        config.androidConfigs.forEach((androidConfig, idx) => {
          if (
            androidConfig.certFingerprints &&
            Array.isArray(androidConfig.certFingerprints)
          ) {
            // Join fingerprints by newlines for display
            config.androidConfigs[idx].certFingerprintsText =
              androidConfig.certFingerprints.join("\n");
          } else {
            config.androidConfigs[idx].certFingerprintsText = "";
          }
        });
      }

      this.formData = config;
      this.dialog = true;
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }
      });
    },
    onDomainChange() {
      // // For custom domain, ensure subdomain isn't required and we set it empty
      // if (!this.isManagedDomain) {
      //   this.formData.subdomain = "";
      // }
    },
    closeDialog() {
      this.dialog = false;
      this.editingIndex = null;
    },
    confirmDeleteDialog(config) {
      this.configToDelete = config;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      if (!this.configToDelete) return;

      this.loading = true;
      try {
        await deleteDeepLinkingConfig(this.configToDelete.domain);
        this.showSuccess("Configuration deleted successfully");
        await this.fetchConfigs();
      } catch (error) {
        this.showError("Failed to delete configuration");
        console.error("Error deleting config:", error);
      } finally {
        this.loading = false;
        this.deleteDialog = false;
        this.configToDelete = null;
      }
    },
    resetIosConfig() {
      this.formData.iosConfigs = [{ teamId: "", bundleId: "", appleAppId: "" }];
    },
    resetAndroidConfig() {
      this.formData.androidConfigs = [
        { packageName: "", certFingerprints: [], certFingerprintsText: "" },
      ];
    },
    copyToClipboard(text) {
      if (!text) return;
      if (navigator && navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => {
          this.showSuccess("Copied to clipboard");
        });
      } else {
        // fallback for older browsers
        const textarea = document.createElement("textarea");
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        this.showSuccess("Copied to clipboard");
      }
    },
    copyDomain(domain) {
      this.copyToClipboard(domain);
    },
    async saveConfig() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) return;
      // Sync all certFingerprints arrays from textarea before saving
      this.formData.androidConfigs.forEach((config) => {
        if (typeof config.certFingerprintsText === "string") {
          const all = config.certFingerprintsText
            .split("\n")
            .map((f) => f.trim())
            .filter((f) => f.length > 0);
          config.certFingerprints = all.filter(
            (f) => f.length >= 64 && f.length <= 95,
          );
          if (all.length !== config.certFingerprints.length) {
            this.error =
              "Each fingerprint must be between 64 and 95 characters.";
          } else {
            this.error = "";
          }
        }
      });
      this.loading = true;
      // Prepare the base data
      const baseData = {
        appName: this.formData.appName,
        iosConfigs: [
          {
            ...this.formData.iosConfigs[0],
          },
        ],
        androidConfigs: [
          {
            certFingerprints: this.formData.androidConfigs[0].certFingerprints,
            packageName: this.formData.androidConfigs[0].packageName,
          },
        ],
      };
      try {
        if (this.editingIndex !== null) {
          // Update existing config
          // Compose full domain for URL
          // Prepare body without domain/subdomain
          const updateBody = { ...baseData };
          await updateDeepLinkingConfig(this.fullDomain, updateBody);
          this.showSuccess("Configuration updated successfully");
        } else {
          // Create new config
          const createBody = {
            ...baseData,
            ...(this.isManagedDomain
              ? { domain: this.selectedDomain }
              : {
                  domain: this.selectedDomain,
                  subdomain: this.formData.subdomain,
                }),
          };
          await createDeepLinkingConfig(createBody);
          this.showSuccess("Configuration created successfully");
          // Remove the pending subdomain from local storage on successful save
          localStorage.removeItem("pendingSubdomain");
          const newSubdomain = this.generateRandomSubdomain();
          this.defaultFormData.subdomain = newSubdomain;
          this.formData.subdomain = newSubdomain;
        }
        this.dialog = false;
        await this.fetchConfigs();
      } catch (error) {
        const errorMsg =
          error.response?.data?.message || "Failed to save configuration";
        this.showError(errorMsg);
        console.error("Error saving config:", error);
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    // Set up form data with either saved subdomain or generate a new one
    const savedSubdomain = localStorage.getItem("pendingSubdomain");
    if (savedSubdomain) {
      this.formData.subdomain = savedSubdomain;
      this.defaultFormData.subdomain = savedSubdomain;
    } else {
      const newSubdomain = this.generateRandomSubdomain();
      this.defaultFormData.subdomain = newSubdomain;
    }
    // Initialize certFingerprintsText for each androidConfig
    this.formData.androidConfigs.forEach((config) => {
      config.certFingerprintsText = (config.certFingerprints || []).join("\n");
    });
  },
  beforeUnmount() {
    // Clean up the pending subdomain if the component is destroyed without saving
    if (this.dialog && this.formData.subdomain) {
      localStorage.setItem("pendingSubdomain", this.formData.subdomain);
    }
    if (this._revealObserver) {
      this._revealObserver.disconnect();
      this._revealObserver = null;
    }
  },
  async created() {
    await this.$store.dispatch("getUserPlan");
    // Load available domains to populate dropdown
    try {
      await this.$store.dispatch("getAvailableDomains");
    } catch (e) {
      // ignore
    }
    await this.fetchConfigs();
    this.$store.commit("setIsLoading", false);
  },
};
</script>

<style scoped>
/* ---------- canvas ---------------------------------------------------- */

.ln-page {
  min-height: 100%;
  background: var(--ln-canvas);
}

.ln-wrap {
  padding: 20px 16px 40px;
}

/* ---------- scroll entry ---------------------------------------------- */

[data-reveal] {
  opacity: 0;
  transform: translateY(12px);
  transition:
    opacity 600ms var(--ln-ease),
    transform 600ms var(--ln-ease);
}

[data-reveal].is-revealed {
  opacity: 1;
  transform: none;
}

@media (prefers-reduced-motion: reduce) {
  [data-reveal],
  [data-reveal].is-revealed {
    opacity: 1;
    transform: none;
    transition: none;
  }
}

/* ---------- page header ------------------------------------------------ */

.ln-head {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 24px;
}

.ln-eyebrow {
  margin: 0 0 10px;
  font-family: var(--ln-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ln-muted);
}

.ln-display {
  margin: 0;
  font-family: sans-serif;
  font-size: 40px;
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: var(--ln-ink);
}

.ln-lede {
  margin: 10px 0 0;
  max-width: 46ch;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ln-muted);
}

.ln-head__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

/* ---------- buttons ---------------------------------------------------- */

.ln-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 16px;
  height: 38px;
  border: 1px solid transparent;
  border-radius: var(--ln-radius-sm);
  background: transparent;
  font-family: sans-serif;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 200ms var(--ln-ease),
    border-color 200ms var(--ln-ease),
    color 200ms var(--ln-ease),
    transform 120ms var(--ln-ease);
}

.ln-btn:active {
  transform: scale(0.98);
}

.ln-btn:focus-visible {
  outline: 2px solid var(--ln-ink);
  outline-offset: 2px;
}

.ln-btn__icon.v-icon {
  font-size: 15px;
  color: inherit;
}

.ln-btn--solid {
  background: var(--ln-accent);
  color: #ffffff;
}

.ln-btn--solid:hover {
  background: var(--ln-accent-hover);
}

.ln-btn--quiet {
  border-color: var(--ln-line);
  background: var(--ln-surface);
  color: var(--ln-ink-2);
}

.ln-btn--quiet:hover {
  background: var(--ln-surface-2);
  border-color: #dcdcdc;
}

.ln-btn--sm {
  height: 28px;
  padding: 0 10px;
  font-size: 12px;
  gap: 4px;
}

.ln-btn--danger {
  background: var(--ln-red-bg);
  color: var(--ln-red-fg);
}

.ln-btn--danger:hover {
  background: #fbdfe1;
}

.ln-btn:disabled {
  opacity: 0.45;
  cursor: default;
  transform: none;
}

.ln-spin {
  animation: ln-spin 900ms linear infinite;
}

@keyframes ln-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.ln-text-link {
  flex: 0 0 auto;
  padding: 0;
  border: 0;
  background: none;
  font-family: sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: var(--ln-ink-2);
  text-decoration: none;
  border-bottom: 1px solid var(--ln-line);
  cursor: pointer;
  transition:
    border-color 200ms var(--ln-ease),
    color 200ms var(--ln-ease);
}

.ln-text-link:hover {
  color: var(--ln-ink);
  border-bottom-color: var(--ln-ink);
}

/* ---------- panel / loading / empty ------------------------------------ */

.ln-panel {
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius);
  background: var(--ln-surface);
  overflow: hidden;
}

.ln-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 56px 24px;
}

.ln-loading__spinner.v-progress-circular {
  color: var(--ln-ink);
}

.ln-loading__text {
  margin: 0;
  font-size: 13px;
  color: var(--ln-muted);
}

.ln-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 24px 64px;
  text-align: center;
}

.ln-empty__art {
  margin-bottom: 20px;
}

.ln-empty__title {
  margin: 0 0 8px;
  font-family: sans-serif;
  font-size: 22px;
  font-weight: 400;
  letter-spacing: -0.02em;
  color: var(--ln-ink);
}

.ln-empty__body {
  margin: 0 0 24px;
  max-width: 38ch;
  font-size: 13px;
  line-height: 1.6;
  color: var(--ln-muted);
}

/* ---------- config cards grid ------------------------------------------- */

.ln-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}

.ln-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius);
  background: var(--ln-surface);
  overflow: hidden;
}

.ln-card__head {
  padding: 18px 18px 16px;
  border-bottom: 1px solid var(--ln-line);
}

.ln-card__app {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.ln-card__app-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius-sm);
  background: var(--ln-surface-2);
  color: var(--ln-muted);
  overflow: hidden;
}

.ln-card__app-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.ln-card__app-info {
  min-width: 0;
  flex: 1 1 auto;
}

.ln-card__app-name {
  margin: 0;
  font-family: sans-serif;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--ln-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ln-card__app-domain {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  min-width: 0;
}

.ln-card__app-domain span {
  font-family: var(--ln-mono);
  font-size: 12px;
  color: var(--ln-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ln-card__body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px 18px;
}

.ln-platform-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.ln-platform-row__icon.v-icon {
  margin-top: 1px;
  font-size: 18px;
  color: var(--ln-muted);
}

.ln-platform-row__icon--android.v-icon {
  color: var(--ln-green-fg);
}

.ln-platform-row__info {
  min-width: 0;
  flex: 1 1 auto;
}

.ln-platform-row__label {
  margin: 0 0 3px;
  font-family: var(--ln-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ln-muted);
}

.ln-platform-row__value {
  margin: 0;
  font-family: var(--ln-mono);
  font-size: 13px;
  color: var(--ln-ink);
  overflow-wrap: anywhere;
}

.ln-platform-row__empty {
  margin: 2px 0 0;
  font-size: 12px;
  color: var(--ln-muted);
}

.ln-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: auto;
  padding: 12px 14px;
  border-top: 1px solid var(--ln-line);
}

/* ---------- setup dropdown menu ----------------------------------------- */

.ln-menu {
  min-width: 180px;
  padding: 6px;
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius);
  background: var(--ln-surface);
  box-shadow: 0 10px 30px rgba(17, 17, 17, 0.1);
}

.ln-menu__item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: 0;
  border-radius: var(--ln-radius-sm);
  background: transparent;
  font-family: sans-serif;
  font-size: 13px;
  color: var(--ln-ink-2);
  text-align: left;
  cursor: pointer;
  transition: background-color 200ms var(--ln-ease);
}

.ln-menu__item:hover {
  background: var(--ln-surface-2);
}

.ln-menu__item .v-icon {
  font-size: 16px;
  color: var(--ln-muted);
}

/* ---------- icon buttons ------------------------------------------------- */

.ln-iconbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
  border: 0;
  border-radius: var(--ln-radius-sm);
  background: transparent;
  color: var(--ln-muted);
  text-decoration: none;
  cursor: pointer;
  transition:
    background-color 200ms var(--ln-ease),
    color 200ms var(--ln-ease);
}

.ln-iconbtn--xs {
  width: 20px;
  height: 20px;
}

.ln-iconbtn--xs .v-icon {
  font-size: 12px;
}

.ln-iconbtn:hover {
  background: var(--ln-surface-2);
  color: var(--ln-ink);
}

.ln-iconbtn:focus-visible {
  outline: 2px solid var(--ln-ink);
  outline-offset: 1px;
}

.ln-iconbtn--danger:hover {
  background: var(--ln-red-bg);
  color: var(--ln-red-fg);
}

.ln-iconbtn .v-icon {
  font-size: 17px;
  color: inherit;
}

.ln-actions {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 2px;
}

/* ---------- tags / banners ------------------------------------------------ */

.ln-banner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: var(--ln-radius-sm);
  font-size: 12.5px;
  line-height: 1.5;
  margin-bottom: 12px;
}

.ln-banner:last-child {
  margin-bottom: 0;
}

.ln-banner .v-icon {
  flex: 0 0 auto;
  font-size: 15px;
  color: inherit;
  margin-top: 1px;
}

.ln-banner--blue {
  background: var(--ln-blue-bg);
  color: var(--ln-blue-fg);
}

.ln-banner--red {
  background: var(--ln-red-bg);
  color: var(--ln-red-fg);
}

.ln-banner code {
  background: rgba(0, 0, 0, 0.06);
  padding: 1px 4px;
  border-radius: 3px;
  font-family: var(--ln-mono);
}

/* ---------- dialogs ------------------------------------------------------ */

.ln-dialog {
  padding: 28px;
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius);
  background: var(--ln-surface);
  max-height: 88vh;
  overflow-y: auto;
}

.ln-dialog__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
}

.ln-dialog__title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 20px;
  font-family: sans-serif;
  font-size: 22px;
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: var(--ln-ink);
}

.ln-dialog__head .ln-dialog__title {
  margin: 0;
}

.ln-dialog__title-icon.v-icon {
  font-size: 20px;
  color: var(--ln-muted);
}

.ln-dialog__field {
  margin-bottom: 16px;
}

.ln-dialog__field--inline {
  margin-bottom: 0;
}

.ln-dialog__field--spaced {
  margin-top: 20px;
}

.ln-dialog__banner {
  margin-bottom: 20px;
}

.ln-dialog__banner--spaced {
  margin-top: 20px;
}

.ln-dialog__note {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 8px 0 0;
  font-size: 12px;
  color: var(--ln-muted);
}

.ln-dialog__label {
  margin: 20px 0 6px !important;
  font-family: var(--ln-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ln-muted) !important;
}

.ln-dialog__target {
  font-family: var(--ln-mono);
  font-size: 13px;
  word-break: break-all;
  color: var(--ln-ink) !important;
}

.ln-dialog__body p {
  margin: 0 0 10px;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ln-ink-2);
}

/* --v-field-border-opacity is .38 by default, which over --ln-line left the
   outline all but invisible; the app draws its own colour, so paint it at
   full strength (same as the Domains dialog). */
.ln-dialog :deep(.v-field__outline) {
  /* --ln-line (#EAEAEA) is a divider colour; as a field border it barely
     showed, so darken it toward the ink. */
  color: color-mix(in srgb, var(--ln-ink) 35%, var(--ln-line));
  --v-field-border-opacity: 1;
}

.ln-dialog :deep(.v-field--focused .v-field__outline) {
  color: var(--ln-accent);
  --v-field-border-opacity: 1;
}

/* Vuetify colours an invalid field's outline and label with the theme error,
   but the two rules above are more specific and were silently winning, so an
   invalid field stayed grey and only the helper text went red. */
.ln-dialog :deep(.v-field--error:not(.v-field--disabled) .v-field__outline) {
  color: rgb(var(--v-theme-error));
}

.ln-dialog :deep(.v-field--error .v-label.v-field-label) {
  color: rgb(var(--v-theme-error));
}

/* .v-label carries opacity: var(--v-medium-emphasis-opacity) in Vuetify 4, so
   --ln-muted was being rendered at 60% and read as washed out. */
.ln-dialog :deep(.v-label.v-field-label) {
  font-family: sans-serif;
  font-size: 13px;
  color: var(--ln-muted);
  opacity: 1;
}

.ln-dialog :deep(.v-field input),
.ln-dialog :deep(.v-field textarea) {
  font-family: sans-serif;
  font-size: 14px;
  color: var(--ln-ink);
}

/* Vuetify draws the placeholder as currentColor at --v-disabled-opacity
   (.38), which over --ln-ink is too faint to read the example values. Only
   the active field, or the label (still sitting inline) shows through it. */
.ln-dialog :deep(.v-field--active input::placeholder),
.ln-dialog :deep(.v-field--active textarea::placeholder) {
  color: var(--ln-muted);
  opacity: 1;
}

.ln-dialog :deep(.v-messages__message) {
  font-size: 11.5px;
}

.ln-field-label {
  margin: 0 0 8px;
  font-family: var(--ln-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ln-muted);
}

.ln-domain-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.ln-domain-row__subdomain {
  flex: 0 1 160px;
}

.ln-domain-row__dot {
  flex: 0 0 auto;
  font-size: 15px;
  color: var(--ln-muted);
}

.ln-domain-row__select {
  flex: 1 1 auto;
  min-width: 0;
}

.ln-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid var(--ln-line);
}

/* ---------- config sections (dialog) ------------------------------------- */

.ln-config-divider {
  height: 1px;
  margin: 24px 0;
  background: var(--ln-line);
}

.ln-config-section-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.ln-config-section-icon.v-icon {
  font-size: 18px;
  color: var(--ln-muted);
}

.ln-config-section-icon--android.v-icon {
  color: var(--ln-green-fg);
}

.ln-config-section-title {
  margin: 0;
  flex: 1 1 auto;
  font-family: sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: var(--ln-ink);
}

.ln-config-fields {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  /* a Vuetify 4 v-input fills the height it is given, so the grid's default
     `stretch` made the Package Name field as tall as the auto-growing
     Certificate Fingerprints textarea beside it */
  align-items: start;
}

.ln-help-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin: 4px 4px 0 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--ln-muted);
  cursor: pointer;
}

.ln-help-trigger:hover {
  color: var(--ln-accent);
}

.ln-help-popover {
  width: 280px;
  max-width: calc(100vw - 32px);
  padding: 14px 16px;
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius);
  background: var(--ln-surface);
  box-shadow: 0 10px 30px rgba(17, 17, 17, 0.1);
}

.ln-help-popover__title {
  margin: 0 0 6px;
  font-family: sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ln-ink);
}

.ln-help-popover__body {
  margin: 0;
  font-size: 12px;
  line-height: 1.6;
  color: var(--ln-muted);
}

.ln-help-popover__body + .ln-help-popover__body {
  margin-top: 8px;
}

.ln-help-popover__body a {
  color: var(--ln-accent);
}

.ln-help-popover__body code {
  font-family: var(--ln-mono);
  font-size: 11px;
  background: var(--ln-surface-2);
  padding: 1px 4px;
  border-radius: 3px;
  color: var(--ln-ink-2);
}

/* ---------- setup drawer -------------------------------------------------- */

.ln-setup-drawer {
  z-index: 9999 !important;
  position: fixed !important;
  max-width: 100vw;
}

.ln-drawer__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--ln-line);
}

.ln-drawer__title {
  margin: 0;
  font-family: sans-serif;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--ln-ink);
}

.ln-drawer__body {
  padding: 20px;
  overflow-y: auto;
  max-height: calc(100vh - 65px);
}

.ln-drawer__body h3 {
  margin: 28px 0 10px;
  font-family: sans-serif;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--ln-ink);
}

.ln-drawer__body h3:first-child {
  margin-top: 0;
}

.ln-drawer__body p {
  margin: 0 0 10px;
  font-size: 13.5px;
  line-height: 1.65;
  color: var(--ln-ink-2);
}

.ln-drawer__body ol {
  margin: 0 0 14px;
  padding-left: 20px;
  font-size: 13.5px;
  line-height: 1.7;
  color: var(--ln-ink-2);
}

.ln-drawer__body li {
  margin-bottom: 4px;
}

.ln-drawer__body a {
  color: var(--ln-accent);
}

.ln-drawer__body code {
  font-family: var(--ln-mono);
  font-size: 0.9em;
  padding: 1px 5px;
  border-radius: 3px;
  background: var(--ln-surface-2);
  color: var(--ln-ink);
}

.ln-code {
  margin: 0 0 14px;
  padding: 12px 14px;
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius-sm);
  background: var(--ln-surface-2);
  font-family: var(--ln-mono);
  font-size: 12px;
  line-height: 1.6;
  color: var(--ln-ink-2);
  overflow-x: auto;
  white-space: pre;
}

/* ---------- breakpoints ------------------------------------------------ */

@media (min-width: 600px) {
  .ln-wrap {
    padding: 28px 20px 56px;
  }

  .ln-display {
    font-size: 52px;
  }

  .ln-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .ln-config-fields {
    grid-template-columns: 1fr 1fr;
  }
}

@media (min-width: 960px) {
  .ln-wrap {
    padding: 36px 24px 64px;
  }

  .ln-head {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 32px;
  }

  .ln-head__actions {
    padding-bottom: 4px;
  }

  .ln-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 680px) {
  .ln-setup-drawer.v-navigation-drawer {
    width: 100vw !important;
  }
}
</style>
